import { DATE_FORMATS } from './constants';

// 日付関連のヘルパー関数 - JST(Asia/Tokyo)固定
const JST = 'Asia/Tokyo';

// JST での各部を取得するユーティリティ
const getJSTParts = (d: Date): { year: number; month: number; day: number; hours: number; minutes: number } => {
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);
  return { year: get('year'), month: get('month'), day: get('day'), hours: get('hour'), minutes: get('minute') };
};

export const formatDate = (date: string | Date, format: string = DATE_FORMATS.DISPLAY): string => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const { year, month, day, hours, minutes } = getJSTParts(d);
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  const hh = String(hours).padStart(2, '0');
  const mi = String(minutes).padStart(2, '0');

  switch (format) {
    case DATE_FORMATS.DISPLAY:
      return `${year}/${mm}/${dd}`;
    case DATE_FORMATS.INPUT:
      return `${year}-${mm}-${dd}`;
    case DATE_FORMATS.DATETIME:
      return `${year}/${mm}/${dd} ${hh}:${mi}`;
    case DATE_FORMATS.TIME:
      return `${hh}:${mi}`;
    default:
      return `${year}/${mm}/${dd}`;
  }
};

export const parseDate = (dateString: string): Date | null => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const isToday = (date: string | Date): boolean => {
  const today = new Date();
  const compareDate = new Date(date);
  return (
    today.getFullYear() === compareDate.getFullYear() &&
    today.getMonth() === compareDate.getMonth() &&
    today.getDate() === compareDate.getDate()
  );
};

export const getRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const compareDate = new Date(date);
  const diffMs = now.getTime() - compareDate.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;
  return formatDate(date);
};

// 文字列操作
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// 配列操作
export const sortByProperty = <T>(array: T[], property: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[property];
    const bVal = b[property];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const group = String(item[key]);
    groups[group] = groups[group] || [];
    groups[group].push(item);
    return groups;
  }, {} as Record<string, T[]>);
};

export const filterBySearch = <T>(array: T[], searchTerm: string, searchFields: (keyof T)[]): T[] => {
  if (!searchTerm.trim()) return array;
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  return array.filter(item =>
    searchFields.some(field => {
      const value = item[field];
      return String(value).toLowerCase().includes(lowerSearchTerm);
    })
  );
};

// 数値・計算
export const roundToDecimal = (num: number, decimals: number = 2): number => {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export const formatNumber = (num: number, locale: string = 'ja-JP'): string => {
  return new Intl.NumberFormat(locale).format(num);
};

export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return roundToDecimal((value / total) * 100);
};

// ファイル操作
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// バリデーション
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateRequired = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};

export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};

// ステータス関連
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    active: 'text-green-600 bg-green-100',
    inactive: 'text-red-600 bg-red-100',
    maintenance: 'text-yellow-600 bg-yellow-100',
    ongoing: 'text-blue-600 bg-blue-100',
    completed: 'text-green-600 bg-green-100',
    cancelled: 'text-red-600 bg-red-100',
    driving: 'text-blue-600 bg-blue-100',
    loading: 'text-orange-600 bg-orange-100',
    unloading: 'text-purple-600 bg-purple-100',
    resting: 'text-gray-600 bg-gray-100',
    refueling: 'text-yellow-600 bg-yellow-100',
    offline: 'text-red-600 bg-red-100',
  };
  
  return statusColors[status] || 'text-gray-600 bg-gray-100';
};

export const getStatusLabel = (status: string, type: string): string => {
  const statusLabels: Record<string, Record<string, string>> = {
    user: {
      active: 'アクティブ',
      inactive: '非アクティブ',
    },
    vehicle: {
      active: '稼働中',
      inactive: '非稼働',
      maintenance: '整備中',
    },
    operation: {
      ongoing: '運行中',
      completed: '完了',
      cancelled: 'キャンセル',
    },
    gps: {
      driving: '運転中',
      loading: '積込中',
      unloading: '積下中',
      resting: '休憩中',
      refueling: '給油中',
      offline: 'オフライン',
    },
  };
  
  return statusLabels[type]?.[status] || status;
};

// LocalStorage操作
export const setStorageItem = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

export const getStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to read from localStorage:', error);
    return defaultValue;
  }
};

export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to remove from localStorage:', error);
  }
};

// デバウンス
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// クリップボード操作
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};